import React, { useEffect, useState } from "react";
import {
  Col,
  Row,
  Card,
  CardBody,
  Button,
  InputGroup,
  Input,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Container,
  InputGroupText,
} from "reactstrap";
import { Triangle } from "react-loader-spinner";
import toast from "react-hot-toast";

import CustomDataTable from "../../../Components/CustomTable/CustomTable";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import DeleteModal from "../../../Components/Common/DeleteModal";
// import DiscountService from "../../../services/OrderingServices/DiscountService";
import MobileDiscountForm from "../Components/MobileDiscountForm";
import DiscountService from "../../../services/OrderingServices/DiscountService";
import useIsSmallScreen from "../../Payment Type/Component/small screen/SmallScreen";
import StatusBadge from "../../../Components/Common/Badge";

const customStyles = {
  headCells: { style: { fontSize: "0.92rem", fontWeight: 610 } },
  rows: { style: { fontSize: "0.9rem" } },
};

const DiscountManagement = () => {
  const [discountList, setDiscountList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addModal, setAddModal] = useState(false);
  const [selectedDiscount, setSelectedDiscount] = useState();
  const [deleteModal, setDeleteModal] = useState(false);
  const isSmallScreen = useIsSmallScreen();

  // Filter state
  const [filterOptions, setFilterOptions] = useState({
    isActive: "",
    discountType: "",
    discountFor: "",
  });

  const [filterText, setFilterText] = useState("");

  const statusConfig = {
    Active: { color: "success", label: "Available" },
    Inactive: { color: "warning", label: "Reserved" },
  };

  // Fetch discounts
  // Fetch discounts
  const fetchDiscounts = async () => {
    try {
      setLoading(true);

      // Build params only if filter is applied
      const params = {};
      if (filterOptions.isActive !== "")
        params.isActive = filterOptions.isActive === "true";
      if (filterOptions.discountType !== "")
        params.discountType = Number(filterOptions.discountType);
      if (filterOptions.discountFor !== "")
        params.discountFor = Number(filterOptions.discountFor);

      const response = await DiscountService.getList(params);
      setDiscountList(response || []);
    } catch (error) {
      console.error("Error fetching discounts:", error);
      toast.error("Failed to load discount list");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const delayDebounce = setTimeout(
      () => {
        fetchDiscounts();
      },
      filterText ? 500 : 0
    ); // only debounce if typing search

    return () => clearTimeout(delayDebounce);
  }, [filterOptions, filterText]);

  useEffect(() => {
    fetchDiscounts();
  }, []);

  // Delete discount
  const toggleDeleteModal = () => setDeleteModal(!deleteModal);
  const handleDeleteClick = (id) => {
    setSelectedDiscount(id);
    toggleDeleteModal();
  };
  const handleDelete = async () => {
    if (selectedDiscount) {
      try {
        await DiscountService.delete(selectedDiscount);
        setDiscountList((prev) =>
          prev.filter((item) => item.id !== selectedDiscount)
        );
        toast.success("Discount deleted successfully");
      } catch (error) {
        toast.error("Failed to delete discount");
      } finally {
        toggleDeleteModal();
        setSelectedDiscount(null);
      }
    }
  };

  // Columns for desktop table
  const columns = [
    {
      name: "S.N",
      selector: (row, index) => index + 1,
      width: "75px",
    },
    {
      name: "Title",
      selector: (row) => row.name,
      sortable: true,
    },
    // {
    //   name: "Description",
    //   selector: (row) => row.description || "-",
    //   sortable: true,
    // },
    {
      name: "Start Time",
      selector: (row) => row.startTime || "-",
      sortable: true,
    },
    {
      name: "End Time",
      selector: (row) => row.endTime || "-",
      sortable: true,
    },
    {
      name: "Discount Type",
      selector: (row) => {
        if (typeof row.discountType === "string") return row.discountType;
        switch (row.discountType) {
          case 1:
            return "Percentage";
          case 2:
            return "Flat";
          default:
            return "-";
        }
      },
      sortable: true,
    },
    {
      name: "Discount For",
      selector: (row) => {
        if (typeof row.discountFor === "string") return row.discountFor;
        switch (row.discountFor) {
          case 1:
            return "Category";
          case 2:
            return "Item";
          case 3:
            return "Entire Menu";
          default:
            return "-";
        }
      },
      sortable: true,
    },
    {
      name: "Discount Time",
      selector: (row) => {
        if (typeof row.discountTime === "string") return row.discountTime;
        switch (row.discountTime) {
          case 1:
            return "Daily";
          case 2:
            return "Weekly";
          case 3:
            return "Monthly";
          case 4:
            return "Yearly";
          case 5:
            return "One-Time";
          default:
            return "-";
        }
      },
      sortable: true,
    },

    {
      name: "Discount Value",
      selector: (row) => row.discountValue || "-",
      sortable: true,
    },
    {
      name: "Active",
      selector: (row) => row.isActive,
      cell: (row) => (
        <StatusBadge status={row.isActive} type="status" pill={true} />
      ),
      sortable: true,
    },
    {
      name: "Action",
      cell: (row) => (
        <div>
          <Button
            color="btn btn-soft-warning"
            className="btn-sm ms-1"
            onClick={() => {
              setAddModal(true);
              setSelectedDiscount(row);
            }}
          >
            <i className="ri-edit-line" />
          </Button>
          <Button
            className="btn btn-soft-danger btn-sm ms-1"
            color="danger"
            onClick={() => handleDeleteClick(row.id)}
          >
            <i className="ri-delete-bin-5-line" />
          </Button>
        </div>
      ),
      center: true,
    },
  ];

  // Filtered data for search
  const filteredData = discountList.filter((discount) => {
    const searchContext = `${discount?.name || ""} ${
      discount?.description || ""
    }`.toLowerCase();
    return searchContext.includes(filterText.toLowerCase());
  });

  return (
    <Container fluid>
      <div className="page-content">
        <BreadCrumb title="Discount Center" pageTitle="System Setup" />

        <div
          className="p-3 p-lg-4 mt-1"
          style={{
            background: "#fff",
            borderRadius: "0.5rem",
            boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
            border: "1px solid #eee",
          }}
        >
          <Row className="justify-content-between align-content-center align-items-center ">
            <Col md={4} className="align-items-center">
              <h5 className="mb-1 fw-bold text-dark ">Discounts List</h5>
            </Col>
            <Col md={8} className="mb-3">
              <div className="d-flex justify-content-end align-content-center align-items-center gap-3">
                <div className="d-flex align-items-center justify-content-end gap-2">
                  <InputGroup>
                    <Input
                      type="select"
                      value={filterOptions.isActive}
                      onChange={(e) =>
                        setFilterOptions((prev) => ({
                          ...prev,
                          isActive: e.target.value,
                        }))
                      }
                    >
                      <option value="">All Status</option>
                      <option value="true">Active</option>
                      <option value="false">Inactive</option>
                    </Input>
                  </InputGroup>

                  <InputGroup>
                    <Input
                      type="select"
                      value={filterOptions.discountType}
                      onChange={(e) =>
                        setFilterOptions((prev) => ({
                          ...prev,
                          discountType: e.target.value,
                        }))
                      }
                    >
                      <option value="">All Types</option>
                      <option value="1">Percentage</option>
                      <option value="2">Flat</option>
                    </Input>
                  </InputGroup>

                  <InputGroup>
                    <Input
                      type="select"
                      value={filterOptions.discountFor}
                      onChange={(e) =>
                        setFilterOptions((prev) => ({
                          ...prev,
                          discountFor: e.target.value,
                        }))
                      }
                    >
                      <option value="">All Discount For</option>
                      <option value="1">Category</option>
                      <option value="2">Item</option>
                      <option value="3">Entire Menu</option>
                    </Input>
                  </InputGroup>
                </div>
                <Button
                  color="success"
                  onClick={() => setAddModal(true)}
                  className="d-flex align-items-center gap-2"
                  style={{ borderRadius: "8px" }}
                >
                  <i className="bx bx-plus"></i> Add
                </Button>
              </div>
            </Col>
            {/* </div> */}
          </Row>

          {isSmallScreen ? (
            loading ? (
              <div className="d-flex justify-content-center my-3">
                <Triangle
                  visible={true}
                  height="80"
                  width="80"
                  color="#5B71B9"
                />
              </div>
            ) : (
              <>
                {" "}
                {filteredData.map((item, index) => (
                  <Card
                    key={item.id || index}
                    className="mb-3 shadow-sm d-block d-lg-none mt-2"
                  >
                    <CardBody>
                      <Row>
                        <Col xs={10}>
                          <h6 className="mb-2 fw-bold">{item.name}</h6>
                          <p className="text-muted small mb-1">
                            {item.description}
                          </p>
                          <p className="mb-0">
                            <strong>Status:</strong>{" "}
                            <span
                              className={`badge ${
                                item.isActive ? "bg-success" : "bg-danger"
                              } fs-11`}
                            >
                              {item.isActive ? "Active" : "Inactive"}
                            </span>
                          </p>
                        </Col>
                        <Col xs={2} className="text-end">
                          <Dropdown
                            isOpen={item.dropdownOpen || false}
                            toggle={(e) => {
                              e.stopPropagation();
                              setDiscountList((prev) =>
                                prev.map((discount) =>
                                  discount.id === item.id
                                    ? {
                                        ...discount,
                                        dropdownOpen: !discount.dropdownOpen,
                                      }
                                    : { ...discount, dropdownOpen: false }
                                )
                              );
                            }}
                          >
                            <DropdownToggle
                              color="primary"
                              outline
                              className="btn-sm shadow-sm rounded-circle"
                            >
                              <i className="ri-more-2-fill"></i>
                            </DropdownToggle>
                            <DropdownMenu end>
                              <DropdownItem
                                onClick={() => {
                                  setAddModal(true);
                                  setSelectedDiscount(item);
                                }}
                              >
                                <i className="ri-edit-line text-warning me-2"></i>{" "}
                                Edit
                              </DropdownItem>
                              <DropdownItem
                                onClick={() => handleDeleteClick(item.id)}
                              >
                                <i className="ri-delete-bin-5-line text-danger me-2"></i>{" "}
                                Delete
                              </DropdownItem>
                            </DropdownMenu>
                          </Dropdown>
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>
                ))}
              </>
            )
          ) : (
            <CustomDataTable
              responsive
              striped
              pagination
              fixedHeader
              persistTableHead
              progressPending={loading}
              customStyles={customStyles}
              progressComponent={
                <div className="my-3 text-center">
                  <Triangle
                    visible={true}
                    height="80"
                    width="80"
                    color="#5B71B9"
                  />
                  <h5 className="mt-1">Loading...</h5>
                </div>
              }
              columns={columns}
              data={filteredData}
              noDataComponent={
                <div className="text-center my-5 w-100">
                  <i className="ri-emotion-unhappy-line display-4 text-secondary" />
                  <h5 className="fw-semibold text-secondary">
                    No data Available
                  </h5>
                  <p className="text-muted mb-0">No recipes found.</p>
                </div>
              }
            />
          )}
        </div>
        {/* Add / Edit Modal */}
        <MobileDiscountForm
          modal={addModal}
          toggleModal={() => {
            setAddModal(false);
            setSelectedDiscount(null);
          }}
          selectedDiscount={selectedDiscount}
          fetchDiscounts={fetchDiscounts}
        />

        {/* Delete Modal */}
        <DeleteModal
          show={deleteModal}
          onDeleteClick={handleDelete}
          onCloseClick={toggleDeleteModal}
          selectedItem={selectedDiscount}
        />
      </div>
    </Container>
  );
};

export default DiscountManagement;
